import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authActivityEvents } from '../../services/authActitivityEvents';
import { itdrApi } from '../../services/api';

const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes for real-time data
const MAX_SSE_EVENTS = 100; // Maximum SSE events to keep in memory

// Fetch KPI Summary
export const fetchKPISummary = createAsyncThunk(
  'authActivity/fetchKPISummary',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { kpiSummary } = getState().authActivity;
      const now = Date.now();

      if (kpiSummary.lastFetched && now - kpiSummary.lastFetched < CACHE_DURATION) {
        return { cached: true };
      }

      const data = await authActivityEvents.kpiSummary("kpi-summary");
      return { cached: false, data };
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to load summary data');
    }
  }
);

// Fetch Actor Events
export const fetchActorEvents = createAsyncThunk(
  'authActivity/fetchActorEvents',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { actorEvents } = getState().authActivity;
      const now = Date.now();

      if (actorEvents.lastFetched && now - actorEvents.lastFetched < CACHE_DURATION) {
        return { cached: true };
      }

      const data = await authActivityEvents.getActorEvents("actor-events");
      return { cached: false, data };
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to load actor events');
    }
  }
);

export const fetchAdapidActivities = createAsyncThunk(
  'authActivity/fetchAdapidActivities',
  async ({ page, limit, search,status }, { rejectWithValue }) => {
    try {
      const params = { page, limit };
      
      // Only add search if it's not null/undefined/empty
      if (search) {
        params.search = search;
      }
      if (status) params.status = status;

      const response = await itdrApi.get('/behaviour-logs', { params });

      return { 
        data: response.data.data.logs || [],
        pagination: {
          totalPages: response.data.data.pages,
          totalCount: response.data.data.total
        }
      };
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to load Adapid data');
    }
  }
);
export const fetchCriticalActivities = createAsyncThunk(
  'authActivity/fetchCriticalActivities',
  async ({ page, limit,search = '' }, { rejectWithValue }) => {
    try {
      const params = { page, limit };
      if (search && search.trim() !== "") {
        params.search = search;
      }
      // Sending params: ?page=1&limit=5
      const response = await itdrApi.get(`/critical-incidents`, {params});
      
      return response.data; 
    } catch (error) {
      return rejectWithValue(error?.response?.data?.error?.message || 'Failed to load critical activities');
    }
  }
);

const authActivitySlice = createSlice({
  name: 'authActivity',
  initialState: {
    // KPI Summary
    kpiSummary: {
      data: null,
      loading: false,
      error: null,
      lastFetched: null,
    },

    // Actor Events
    actorEvents: {
      data: null,
      loading: false,
      error: null,
      lastFetched: null,
    },

    // Adapid Activities
    adapidActivities: {
      data: [], // Changed from {} to []
      loading: false,
      error: null,
      pagination: { totalPages: 0, totalCount: 0 },
      searchQuery: '', // Added search query field
      statusFilter: '',
    },
    // Critical Activities State
    criticalActivities: {
      data: [],
      loading: false,
      error: null,
      lastFetched: null,
      searchQuery: '',
    },

    // SSE Real-time Data
    sseData: {
      events: [],
      loading: true,
      connectionStatus: 'connecting', // 'connecting' | 'connected' | 'error' | 'disconnected'
      currentPage: 1,
      pageSize: 5,
    },

    // Selected Event (for detail view)
    selectedEvent: null,
    detailLoading: false,

    // Active Tab
    activeTab: 'normal', // 'normal' | 'adapid' | 'critical'

    // Pagination for Adapid
    currentPage: 1,
    pageSize: 5,
  },
  reducers: {
    // Set selected event
    setSelectedEvent: (state, action) => {
      state.selectedEvent = action.payload;
      state.detailLoading = false;
    },

    // Clear selected event
    clearSelectedEvent: (state) => {
      state.selectedEvent = null;
      state.detailLoading = false;
    },

    // Set active tab
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
      state.currentPage = 1;
    },

    // Set current page (for Adapid)
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },

    // SSE Actions
    setSseLoading: (state, action) => {
      state.sseData.loading = action.payload;
    },

    setSseConnectionStatus: (state, action) => {
      state.sseData.connectionStatus = action.payload;
    },

    addSseEvent: (state, action) => {
      // Add new event to the beginning
      state.sseData.events = [action.payload, ...state.sseData.events];
      
      // Limit total events to prevent memory overflow
      if (state.sseData.events.length > MAX_SSE_EVENTS) {
        state.sseData.events = state.sseData.events.slice(0, MAX_SSE_EVENTS);
      }
      
      // Reset to first page when new event arrives
      state.sseData.currentPage = 1;
    },

    setSseEvents: (state, action) => {
      state.sseData.events = action.payload;
    },

    setSsePage: (state, action) => {
      state.sseData.currentPage = action.payload;
    },

    clearSseEvents: (state) => {
      state.sseData.events = [];
      state.sseData.currentPage = 1;
    },

    resetSseState: (state) => {
      state.sseData = {
        events: [],
        loading: true,
        connectionStatus: 'connecting',
        currentPage: 1,
        pageSize: 5,
      };
    },

    // Cache management
    clearKPICache: (state) => {
      state.kpiSummary.lastFetched = null;
    },

    clearActorEventsCache: (state) => {
      state.actorEvents.lastFetched = null;
    },

    clearAdapidCache: (state) => {
      state.adapidActivities.lastFetched = null;
    },

    clearAllCaches: (state) => {
      state.kpiSummary.lastFetched = null;
      state.actorEvents.lastFetched = null;
      state.adapidActivities.lastFetched = null;
    },

    setCriticalSearchQuery: (state, action) => {
      state.criticalActivities.searchQuery = action.payload;
      state.currentPage = 1; // Reset page to 1 when searching
    },

    setAdapidSearchQuery: (state, action) => {
      state.adapidActivities.searchQuery = action.payload;
      state.currentPage = 1; // Reset to page 1 on search
    },
    setAdapidStatusFilter: (state, action) => {
      state.adapidActivities.statusFilter = action.payload;
      state.currentPage = 1; // Always reset to page 1 when filter changes
    },

    removeCriticalIncident: (state, action) => {
      const idToRemove = action.payload;
      state.criticalActivities.data = state.criticalActivities.data.filter(
        (item) => item.incident_id !== idToRemove
      );
      // Update count so pagination UI stays synced
      if (state.criticalActivities.pagination.total_records > 0) {
        state.criticalActivities.pagination.total_records -= 1;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // KPI Summary
      .addCase(fetchKPISummary.pending, (state) => {
        state.kpiSummary.loading = true;
        state.kpiSummary.error = null;
      })
      .addCase(fetchKPISummary.fulfilled, (state, action) => {
        state.kpiSummary.loading = false;
        const { cached, data } = action.payload;

        if (!cached) {
          state.kpiSummary.data = data;
          state.kpiSummary.lastFetched = Date.now();
        }
      })
      .addCase(fetchKPISummary.rejected, (state, action) => {
        state.kpiSummary.loading = false;
        state.kpiSummary.error = action.payload;
      })

      // Actor Events
      .addCase(fetchActorEvents.pending, (state) => {
        state.actorEvents.loading = true;
        state.actorEvents.error = null;
      })
      .addCase(fetchActorEvents.fulfilled, (state, action) => {
        state.actorEvents.loading = false;
        const { cached, data } = action.payload;

        if (!cached) {
          state.actorEvents.data = data;
          state.actorEvents.lastFetched = Date.now();
        }
      })
      .addCase(fetchActorEvents.rejected, (state, action) => {
        state.actorEvents.loading = false;
        state.actorEvents.error = action.payload;
      })
      .addCase(fetchAdapidActivities.pending, (state) => {
        state.adapidActivities.loading = true;
        state.adapidActivities.error = null;
      })
      .addCase(fetchAdapidActivities.fulfilled, (state, action) => {
        state.adapidActivities.loading = false;
        state.adapidActivities.data = action.payload.data;
        state.adapidActivities.pagination = action.payload.pagination;
      })
      .addCase(fetchAdapidActivities.rejected, (state, action) => {
        state.adapidActivities.loading = false;
        state.adapidActivities.error = action.payload;
        state.adapidActivities.data = []; // Clear data on error
      })
      .addCase(fetchCriticalActivities.pending, (state) => {
        state.criticalActivities.loading = true;
      })
      .addCase(fetchCriticalActivities.fulfilled, (state, action) => {
        state.criticalActivities.loading = false;
        // Store the data and the pagination metadata from the API
        state.criticalActivities.data = action.payload.data;
        state.criticalActivities.pagination = action.payload.pagination;
      })
      .addCase(fetchCriticalActivities.rejected, (state, action) => {
        state.criticalActivities.loading = false;
        state.criticalActivities.error = action.payload;
      });
        },
});

export const {
  setSelectedEvent,
  clearSelectedEvent,
  setActiveTab,
  setCurrentPage,
  setSseLoading,
  setSseConnectionStatus,
  addSseEvent,
  setSseEvents,
  setSsePage,
  clearSseEvents,
  resetSseState,
  clearKPICache,
  clearActorEventsCache,
  clearAdapidCache,
  clearAllCaches,
  removeCriticalIncident,
  setCriticalSearchQuery,
  setAdapidSearchQuery,
  setAdapidStatusFilter,
} = authActivitySlice.actions;

export default authActivitySlice.reducer;